import { 
  FormControl, 
  FormLabel, 
  Grid, 
  Button, 
  TextField, 
  MenuItem,
  Box,
  IconButton
} from '@mui/material';
import { useEffect, useState } from 'react';
import { ProgramOffice, Role, User } from '../../services/Types';
import { formatDate } from '../../services/Helper';
import { deleteUser, updateApprovalStatus, updateRole, updateUser, updatePassword } from '../../services/AuthService';
import { DialogNewApprover } from '../Dialogs/NewApprover';
import { NewPassword } from '../Dialogs/NewPassword';
import { flexItemRightGap, flexItemSpaceBetween } from '../../global'
import DropDownSelectorProgramOffice from '../DropDownSelector/DropDownSelectorProgramOffice';
import { useAllUsers } from '../../hooks/useAllUsers';
import { useProgramOfficeData } from '../../hooks/useProgramOfficeData';
import { useToast } from '../../hooks/useToast';
import LockIcon from '@mui/icons-material/Lock';

interface UserEditFormProps {
  accountSelected: User;
  onSave?: () => void;
  onDelete?: () => void;
}

export const UserEditForm = ({ accountSelected, onSave, onDelete }: UserEditFormProps) => {
  const [editedUser, setEditedUser] = useState<User>(accountSelected);
  const [showNewApproverDialog, setShowNewApproverDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [newApprover, setNewApprover] = useState<number>(0);
  const { resetCache } = useAllUsers();
  const { resetProgramOfficeCache } = useProgramOfficeData();
  const { showToast, renderToast } = useToast();


  useEffect(() => {
    setEditedUser(accountSelected);
  }, [accountSelected]);

  const handleSave = async () => {
    // TODO: Need to move to new useUser hook, compare if API calls are needed
    try {
      const result = await updateUser(
        editedUser.id,
        editedUser.firstName,
        editedUser.lastName,
        editedUser.email
      );
      if (result.data.updateUser.statusCode !== 200) {
        throw new Error(result.data.updateUser.message || 'Error updating user');
      }

      const approvalStatusResult = await updateApprovalStatus(
        editedUser.id,
        editedUser.approvalStatus
      )
      if (approvalStatusResult.data.updateApprovalStatus.statusCode !== 200) {
        throw new Error(approvalStatusResult.data.updateApprovalStatus.message || 'Error updating approval status');
      }

      const eventOwner = newApprover ? newApprover : 0;
      const response = await updateRole(
        editedUser.id,
        editedUser.role as Role,
        editedUser.subRole,
        editedUser.approverLevel,
        editedUser.programOffice?.id ?? 0,
        eventOwner
      )
      if (response.data.updateRole.statusCode === 200) {
        console.log('Role updated successfully');
        // only onSave if the user is fully updated
        setShowNewApproverDialog(false);
        setNewApprover(0);
        resetCache();
        // Refresh program office cache so Level One Approver dropdowns show correct data
        resetProgramOfficeCache();
        showToast(true, 'User updated successfully', 'success');
        onSave?.();
      } else if (response.data.updateRole.statusCode === 400) {
        // If user has pending event approvals, show the select new approver dialog
        document.body.focus()
        setShowNewApproverDialog(true)
      } else {
        throw new Error(response.data.updateRole.message || 'Error updating role');
      }
    } catch (error) {
      console.log(error);
      const message = error instanceof Error ? error.message : String(error);
      showToast(true, message, 'error');
    }
  };

  const handleDelete = async () => {
    try {
      // TODO: Need to get eventOwner from the user, need DialogDeleteUserg
      const eventOwner = 0;
      const userId = editedUser.id;
      const response = await deleteUser(userId, eventOwner)
      if (response.data.deleteUser.statusCode !== 200) {
        throw new Error('Error deleting user');
      }
      console.log('User deleted successfully');
      resetCache();
    } catch (error) {
      console.log(error);
    }
  };

  const handleNewApproverSave = (newApprover: number) => {
    setShowNewApproverDialog(false);
    console.log("handleNewApproverSave", {
      newApprover: newApprover,
      editedUser: editedUser
    });
    if (newApprover !== 0) {
      setNewApprover(newApprover);
      handleSave();
    }
  }

  const handleNewApproverCancel = () => {
    setShowNewApproverDialog(false);
    setNewApprover(0);
  }

  return (
    <FormControl fullWidth sx={{ backgroundColor: "#F3F4F3", padding: "20px", marginBottom: 0 }}>
      <FormLabel>User Fields</FormLabel>
      <Grid container spacing={2}>
        <Grid item xs={12} style={flexItemSpaceBetween}>
          <span>User since: {formatDate(accountSelected.createdAt, 'date') as string}</span>
          <Box style={flexItemRightGap}>
            <Button variant="contained" color="primary" onClick={handleSave}>
              Save
            </Button>
            <Button variant="outlined" color="error" onClick={handleDelete}>
              Delete User
            </Button>
          </Box>
        </Grid>
        <Grid item xs={3}>
          <TextField
            label="First Name"
            value={editedUser.firstName ?? ''}
            disabled={editedUser.firstName === undefined}
            onChange={(e) => {
              setEditedUser({ ...editedUser, firstName: e.target.value });
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            label="Last Name"
            value={editedUser.lastName ?? ''}
            disabled={editedUser.lastName === undefined}
            onChange={(e) => {
              setEditedUser({ ...editedUser, lastName: e.target.value });
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            label="Email"
            value={editedUser.email ?? ''}
            disabled={editedUser.email === undefined}
            onChange={(e) => {
              setEditedUser({ ...editedUser, email: e.target.value });
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              label="Status"
              select
              value={editedUser.approvalStatus ?? ''}
              disabled={editedUser.approvalStatus === undefined}
              onChange={(e) => {
                console.log(e.target.value);
                setEditedUser({ ...editedUser, approvalStatus: e.target.value });
              }}
            >
              <MenuItem value=""></MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="APPROVED">Approved</MenuItem>
              <MenuItem value="DECLINED">Declined</MenuItem>
            </TextField>
            <IconButton
              onClick={() => setShowPasswordDialog(true)}
              size="small"
              sx={{ color: '#006BD7', marginBottom: '20px' }}
            >
              <LockIcon />
            </IconButton>
          </Box>
        </Grid>
        <Grid item xs={2}>
          <TextField
            label="Role"
            select
            value={editedUser.role ?? ''}
            disabled={editedUser.role === undefined}
            onChange={(e) => {
              setEditedUser({ ...editedUser, role: e.target.value });
            }}
          >
            <MenuItem value=""></MenuItem>
            <MenuItem value="ADMIN">Admin</MenuItem>
            <MenuItem value="REQUESTER">Requester</MenuItem>
            <MenuItem value="APPROVER">Approver</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={2}>
          <TextField
            label="Subrole"
            select
            value={editedUser.subRole ?? ''}
            disabled={editedUser.role !== 'APPROVER'}
            onChange={(e) => {
              setEditedUser({ ...editedUser, subRole: e.target.value });
            }}
          >
            <MenuItem value="">None</MenuItem>
            <MenuItem value="PRIMARY">Primary</MenuItem>
            <MenuItem value="ALTERNATE">Alternate</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={2}>
          <TextField
            label="Level"
            select
            value={editedUser.approverLevel ?? 0}
            disabled={editedUser.role !== 'APPROVER'}
            onChange={(e) => {
              setEditedUser({ ...editedUser, approverLevel: parseInt(e.target.value) });
            }}
          >
            <MenuItem value="0">None</MenuItem>
            <MenuItem value="1">Level 1</MenuItem>
            <MenuItem value="2">Level 2</MenuItem>
            <MenuItem value="3">Level 3</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={6}>
          <DropDownSelectorProgramOffice
            selectedOffice={editedUser?.programOffice ?? null}
            onOfficeChange={(office) => {
              setEditedUser({ ...editedUser, programOffice: office ?? {} as ProgramOffice });
            }}
          />
        </Grid>
      </Grid>
      <DialogNewApprover
        open={showNewApproverDialog}
        onSave={(newApprover: number) => handleNewApproverSave(newApprover)}
        onCancel={handleNewApproverCancel}
        currentApprover={accountSelected}
      />
      <NewPassword
        open={showPasswordDialog}
        onClose={() => setShowPasswordDialog(false)}
        user={accountSelected}
        onSuccess={() => {
          setShowPasswordDialog(false);
        }}
      />
      {renderToast()}
    </FormControl>
  );
}; 